import React, { useState, useEffect, memo } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Document, useGetDocuments } from '@/stores/documentLibraryStore'

interface DocumentLibraryDialogProps {
  visible: boolean
  onHide: () => void
  onDocumentsSelected: (markdown: string) => void
  currentContent?: string
  withPreview?: boolean
}

const DocumentLibraryDialog: React.FC<DocumentLibraryDialogProps> = ({
  visible,
  onHide,
  onDocumentsSelected,
  currentContent = '',
  withPreview = true,
}) => {
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([])
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [includeMetadata, setIncludeMetadata] = useState(false)
  const [appendToExisting, setAppendToExisting] = useState(true)
  const [combinedPreview, setCombinedPreview] = useState('')
  const [categories, setCategories] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>([])
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  // Get documents from store
  const {
    documents,
    documentsLoading,
    documentsFetchSuccess,
    documentsFetchError,
    fetchDocuments,
  } = useGetDocuments()

  // Fetch documents when dialog becomes visible
  useEffect(() => {
    if (visible) {
      fetchDocuments()
    }
  }, [visible, fetchDocuments])

  // Reset selections when dialog is opened
  useEffect(() => {
    if (visible) {
      setActiveTabIndex(0)
    }
  }, [visible])

  // Extract categories and tags when documents change
  useEffect(() => {
    if (documents) {
      const uniqueCategories = Array.from(
        new Set(documents.map((doc) => doc.category))
      ).filter(Boolean)
      setCategories(uniqueCategories)

      const allTags = documents.flatMap((doc) => doc.tags || [])
      const uniqueTags = Array.from(new Set(allTags)).filter(Boolean)
      setTags(uniqueTags)
    }
  }, [documents])

  // Filter documents based on search term, category, and tags
  useEffect(() => {
    if (!documents) {
      setFilteredDocuments([])
      return
    }

    let filtered = [...documents]

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(term) ||
          doc.purpose.toLowerCase().includes(term) ||
          (doc.documentKey && doc.documentKey.toLowerCase().includes(term))
      )
    }

    // Filter by category
    if (categoryFilter) {
      filtered = filtered.filter((doc) => doc.category === categoryFilter)
    }

    // Filter by tags
    if (tagFilter.length > 0) {
      filtered = filtered.filter((doc) =>
        tagFilter.some((tag) => doc.tags && doc.tags.includes(tag))
      )
    }

    setFilteredDocuments(filtered)
  }, [documents, searchTerm, categoryFilter, tagFilter])

  // Generate combined markdown preview
  useEffect(() => {
    if (selectedDocuments.length === 0) {
      setCombinedPreview('')
      return
    }

    let combined = appendToExisting ? currentContent : ''

    // Add separator if appending and current content exists
    if (appendToExisting && currentContent && combined.length > 0) {
      combined += '\n\n---\n\n'
    }

    // Combine selected documents
    selectedDocuments.forEach((doc, index) => {
      if (index > 0 || (appendToExisting && currentContent)) {
        combined += '\n\n---\n\n'
      }

      if (includeMetadata) {
        combined += `# ${doc.title}\n\n`
        combined += `> **Category:** ${doc.category}\n\n`
        combined += `> **Purpose:** ${doc.purpose}\n\n`
        if (doc.extraContext) {
          combined += `> **Context:** ${doc.extraContext}\n\n`
        }
        if (doc.tags && doc.tags.length > 0) {
          combined += `> **Tags:** ${doc.tags.join(', ')}\n\n`
        }
        combined += '---\n\n'
      }

      combined += doc.currentMarkdown
    })

    setCombinedPreview(combined)
  }, [selectedDocuments, includeMetadata, appendToExisting, currentContent])

  // Toggle document selection
  const handleDocumentToggle = (doc: Document, selected: boolean) => {
    if (selected) {
      setSelectedDocuments([...selectedDocuments, doc])
    } else {
      setSelectedDocuments(selectedDocuments.filter((d) => d.id !== doc.id))
    }
  }

  // Apply selected documents
  const handleApply = () => {
    onDocumentsSelected(combinedPreview)
    toast.success('Documents selected successfully')
    onHide()
  }

  //console.log('Categories: ', categories)

  // Data table header with filters
  const renderHeader = () => {
    return (
      <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
        <h3 className="m-0">Document Templates</h3>
        <div className="flex gap-2">
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-inputtext-sm"
          />
          <Select
            value={categoryFilter}
            onValueChange={(e) => setCategoryFilter(e.value)}
          >
            <SelectTrigger className="w-12rem p-inputtext-sm">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.length > 0 ? (
                categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-categories" disabled>
                  No categories available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select
            value={tagFilter}
            onValueChange={(value) => setTagFilter(value)}
            multiple
          >
            <SelectTrigger className="w-12rem p-inputtext-sm">
              <SelectValue placeholder="Tags" />
            </SelectTrigger>
            <SelectContent>
              {tags.length > 0 ? (
                tags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-tags" disabled>
                  No tags available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
    )
  }

  // Tag template for data table
  const tagTemplate = (rowData: Document) => {
    return (
      <div className="flex flex-wrap gap-1">
        {rowData.tags &&
          rowData.tags.map((tag) => (
            <Badge key={tag} variant="outline" className="mr-1">
              {tag}
            </Badge>
          ))}
      </div>
    )
  }

  // Selection template for data table
  const selectionTemplate = (rowData: Document) => {
    const isSelected = selectedDocuments.some((doc) => doc.id === rowData.id)
    return (
      <Checkbox
        checked={isSelected}
        onCheckedChange={(checked) =>
          handleDocumentToggle(rowData, checked as boolean)
        }
      />
    )
  }

  // Date formatting template
  const dateTemplate = (
    rowData: Document,
    field: 'createdAt' | 'updatedAt'
  ) => {
    return new Date(rowData[field]).toLocaleString()
  }

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="sm:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>Document Library</DialogTitle>
          <DialogDescription>Load document template(s)</DialogDescription>
        </DialogHeader>

        {/* Preserve the exact structure and functionality of filters and search */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-[300px]"
            />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-categories" disabled>
                    No categories available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {documentsLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Progress value={undefined} className="w-[200px]" />
            <p className="mt-2 text-sm text-muted-foreground">
              Loading documents...
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow
                  key={doc.id}
                  className={
                    selectedDocuments.some((d) => d.id === doc.id)
                      ? 'bg-muted'
                      : ''
                  }
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedDocuments.some((d) => d.id === doc.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDocuments([...selectedDocuments, doc])
                        } else {
                          setSelectedDocuments(
                            selectedDocuments.filter((d) => d.id !== doc.id)
                          )
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{doc.title}</TableCell>
                  <TableCell>{doc.type}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {doc.tags?.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(doc.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={onHide}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!selectedDocuments.length}>
            Select Documents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default memo(DocumentLibraryDialog)
